"use client";
import {
  EmailCaptureHero,
  FilmstripNarrativeStorySection,
  MegaFooter,
  NavbarMegaMenu,
  NewsletterCta,
  PathwayRibbonStorySection,
  PricingSection,
  ServicesShowcaseSection,
  StackedProofStorySection,
  TestimonialWall,
} from "@/components/sections"
import { ThemeScope } from "@/lib/theme"

const tracks = [
  { title: "Product Design Fundamentals", description: "A 12-week live cohort for designers moving into product. Twelve students. Two instructors. One real client project per cohort. Held twice a year." },
  { title: "Senior Design Systems", description: "An 8-week intensive for designers building or maintaining design systems at scale. Tokens, primitives, governance, contribution flows, and stakeholder politics." },
  { title: "Design Engineering Crash Course", description: "A 6-week self-paced program for designers learning to ship in React. Tailwind, shadcn/ui, Framer Motion, and the parts of TypeScript designers actually need." },
  { title: "Research Operations", description: "A 4-week live workshop on the boring-important parts of research: participant ops, panel hygiene, repository, archive culture, and proving research's impact." },
  { title: "Portfolio Studio", description: "A 4-week 1:1 portfolio rebuild. One senior practitioner. Three case studies. The version of your work that gets hiring managers to reply." },
  { title: "Career Coaching", description: "Six 1:1 sessions over three months. Salary negotiation, interview prep, scoping new roles, and the conversation about whether to stay or leave." },
]

const learningJourney = [
  { eyebrow: "Week 0", title: "Pre-work and cohort intros.", description: "You get the syllabus, the reading list, your cohort's Slack channel, and 60 minutes with your assigned mentor. Most cohorts meet for an informal kickoff happy hour the Friday before.", metric: "12", label: "in your cohort" },
  { eyebrow: "Weeks 1–4", title: "Foundations: the actual craft.", description: "Two live sessions per week, 90 minutes each. Lecture-then-critique format. Weekly assignment grounded in a real (anonymized) client problem. Office hours every Friday with one of the instructors.", metric: "4hr", label: "live per week" },
  { eyebrow: "Weeks 5–10", title: "Projects: build something that ships.", description: "Six weeks on one real client project. Your team of four owns scope, research, design, and handoff. The client reviews your work. Critique by senior practitioners. Stakes are intentionally high.", metric: "1", label: "real client" },
  { eyebrow: "Weeks 11–12", title: "Portfolio + presentation.", description: "Two weeks to turn what you built into the case study version of your work. Final presentation to the cohort, the instructors, and a small panel of hiring partners. Recordings are yours forever.", metric: "1", label: "case study" },
  { eyebrow: "After", title: "Lifetime alumni community.", description: "Slack channel for 1,400+ alumni. Job board where partners post first. Quarterly virtual reunions. A real network of people who shipped together, not just sat in a Zoom together.", metric: "1,400+", label: "alumni" },
]

const typicalWeek = [
  { eyebrow: "Monday 7pm ET", title: "Live lecture — 90 minutes, recorded.", description: "Instructor walks through the week's topic with real examples. Q&A baked in. You can attend live or watch the recording the same night." },
  { eyebrow: "Tuesday–Thursday", title: "Assignment work, in your own hours.", description: "Most students spend 4–6 hours across the week on the assignment. Cohort Slack stays active — students unblock each other constantly." },
  { eyebrow: "Thursday 7pm ET", title: "Live critique — 90 minutes, your work on the screen.", description: "Half the cohort presents each week. Critique is rigorous but kind. You'll watch four critiques and present in one, then alternate the next week." },
  { eyebrow: "Friday office hours", title: "Open Zoom with an instructor.", description: "Drop in for 1:1 help, a deeper conversation about your work, or just to ask 'is this normal?' (it almost always is)." },
  { eyebrow: "Weekend", title: "Optional: cohort study groups.", description: "Self-organized. Some cohorts meet at coffee shops. Some do virtual coworking. Some don't. All formats valid." },
]

const studentArc = [
  { eyebrow: "Before", title: "Sara was a senior product designer at a Series A.", description: "Five years of experience. Strong execution. Stuck on systems thinking and zero exposure to the platform-level decisions her CTO was making.", metric: "5yr", label: "exp at start" },
  { eyebrow: "Week 6", title: "Sara is co-designing a token system for a client's e-commerce rewrite.", description: "Her team of four is shipping spec, the critiques from senior practitioners are landing, and she's reading the design-systems writing she used to skim.", metric: "4", label: "team size" },
  { eyebrow: "Week 12", title: "Final presentation. Hiring partner panel.", description: "Sara presents the rewrite. Two of the hiring partners ask her follow-up questions. The case study is on her portfolio the next day.", metric: "2", label: "panel asks" },
  { eyebrow: "Month 4", title: "Sara is a Staff Designer at one of the hiring partners.", description: "Salary up 38%. Scope expanded to lead the design-system practice. She still attends the alumni Slack three times a week.", metric: "+38%", label: "salary change" },
]

const pricingPlans = [
  {
    name: "Self-Paced",
    description: "For learners on their own time. Lifetime access, video lessons, written assignments, and Slack-community support.",
    price: "$890",
    period: "one-time",
    features: ["Full course content (all tracks)", "Lifetime access to materials", "Slack community access", "Written feedback on portfolio submissions"],
    cta: { label: "Buy self-paced access", href: "#self-paced" },
  },
  {
    name: "Cohort",
    description: "For learners who want live instruction, real client projects, and a high-accountability environment with a small group.",
    price: "$3,400",
    period: "per cohort",
    features: ["All self-paced content included", "12-week live cohort with 11 peers", "2 senior instructors, 1 mentor", "Real client project (paid externship)", "Hiring partner panel + lifetime alumni access"],
    cta: { label: "Apply to next cohort", href: "#cohort" },
    highlighted: true,
    badge: "Most popular",
  },
  {
    name: "Team",
    description: "For 3+ designers. Volume rates, custom client-project alignment, and an internal showcase at the end. Most enterprise teams sponsor 4–8 designers per cohort.",
    price: "Custom",
    features: ["Volume pricing (10–40% off)", "Dedicated cohort if team is large enough", "Custom client-project alignment", "Quarterly check-ins with our team lead"],
    cta: { label: "Talk to our team", href: "#team-pricing" },
  },
]

export default function HomePage() {
  return (
    <ThemeScope preset="retrofuture-culture">
      <NavbarMegaMenu
        brand="Atlas Learning"
        items={[
          {
            label: "Programs",
            href: "#programs",
            dropdownColumns: [
              {
                title: "Live cohorts",
                items: [
                  { label: "Product Design Fundamentals", href: "#fundamentals", description: "12-week live cohort. Twelve students. Real client project." },
                  { label: "Senior Design Systems", href: "#systems", description: "8-week intensive on tokens, governance, and contribution." },
                  { label: "Research Operations", href: "#research", description: "4-week workshop on the boring-important parts of research." },
                ],
              },
              {
                title: "Self-paced",
                items: [
                  { label: "Design Engineering", href: "#design-eng", description: "6 weeks. React, Tailwind, shadcn/ui for designers." },
                  { label: "Portfolio Studio", href: "#portfolio", description: "4-week 1:1 rebuild with a senior practitioner." },
                  { label: "Career Coaching", href: "#coaching", description: "Six 1:1 sessions over three months." },
                ],
              },
            ],
            featuredCard: { eyebrow: "Next cohort", title: "Product Design Fundamentals", description: "October 6 start. Applications open now. Twelve spots, twelve practitioners.", href: "#fundamentals" },
          },
          { label: "Alumni", href: "#alumni" },
          { label: "Hiring partners", href: "#partners" },
          { label: "Pricing", href: "#pricing" },
          { label: "About", href: "/about" },
        ]}
        ctas={[
          { label: "Sign in", href: "/sign-in", variant: "ghost" },
          { label: "Apply now", href: "#apply", variant: "primary" },
        ]}
        announcement={{
          text: "Applications for the October cohort close September 18. 4 spots left.",
          linkLabel: "Apply now",
          href: "#apply",
        }}
      />
      <main id="main-content">
        <EmailCaptureHero
          eyebrow="Live cohorts · Self-paced tracks · Real outcomes"
          headline="Design and product education that ships work, not just knowledge."
          subheadline="Twelve-week live cohorts taught by working senior practitioners. Real client projects. A hiring partner network of 47 product orgs. Self-paced tracks too, if your schedule needs it."
          primaryCta={{ label: "Apply to next cohort", href: "#apply" }}
          secondaryCta={{ label: "Explore self-paced tracks", href: "#self-paced" }}
          trustMicrocopy="Next cohort: October 6  ·  Applications close September 18  ·  Twelve spots, twelve practitioners"
          form={{
            fields: [{ name: "email", label: "Email", type: "email", placeholder: "you@example.com", required: true }],
            submitLabel: "Get the syllabus",
            successMessage: "Syllabus on its way to your inbox.",
          }}
          media={{
            type: "image",
            src: "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?auto=format&fit=crop&w=2200&q=92",
            alt: "Designers around a table reviewing critique work, laptops open",
          }}
        />
        <PathwayRibbonStorySection
          eyebrow="What 12 weeks looks like"
          title="From pre-work to alumni network — every week mapped."
          description="The Atlas cohort is built around four phases. Each phase has a clear deliverable and a clear point at which you can drop or pivot if it isn't working."
          steps={learningJourney}
        />
        <ServicesShowcaseSection
          eyebrow="Programs"
          title="Six tracks. Live or self-paced. Career-aware."
          description="We picked these six programs because they're the moments in a designer's career when education compounds the hardest — and where most existing options either oversell or underdeliver."
          services={tracks}
        />
        <StackedProofStorySection
          eyebrow="A typical week as a cohort student"
          title="Live instruction, async work, real critique."
          description="Four hours of live class. Four-to-six hours of assignment work. One office-hour Friday. The rest is your life — we built this for working designers."
          frames={typicalWeek}
        />
        <TestimonialWall
          eyebrow="Alumni"
          title="What 1,400+ alumni say a year after graduating."
          description="Survey data from the most recent 200 alumni, plus selected quotes from longer conversations. Salary and title bumps are the headline; the alumni network is the quiet long-term value."
          testimonials={[
            { quote: "I was at a senior level and stuck. After Atlas, I was leading a design systems team three months later. The hiring partner network was the unfair advantage no one talks about.", name: "Sara K.", role: "Staff Designer, Notion (Atlas '24)" },
            { quote: "I expected the cohort to be a glorified course. It was the most intense and most rewarding twelve weeks of my career. The critiques alone were worth the price.", name: "Wesley M.", role: "Design Lead, Stripe (Atlas '23)" },
            { quote: "Career coaching paid for itself in the first negotiation. I went in asking for 15% more; I walked out with 38% and a level promotion.", name: "Priya R.", role: "Senior PM, Linear (Atlas '24)" },
            { quote: "I'm an intro and the live format scared me. I was wrong. Twelve people, twelve weeks — by week three you know everyone's work and they know yours. Tiny is the feature.", name: "Marcus J.", role: "Senior Designer, Vercel (Atlas '25)" },
          ]}
        />
        <FilmstripNarrativeStorySection
          eyebrow="One student, one year"
          title="What Atlas actually looked like for Sara."
          description="Sara K. graduated from the Spring 2024 cohort. We tracked her career through to four months after graduation. The arc is typical — but every cohort student writes their own."
          frames={studentArc}
        />
        <PricingSection
          eyebrow="Pricing"
          title="Pick the format that matches how you actually learn."
          description="Cohort, self-paced, or team — same content, different structure. We do not offer scholarships at the Atlas pricepoint, but our hiring partners reimburse cohort tuition for most full-time roles."
          plans={pricingPlans}
        />
        <NewsletterCta
          eyebrow="The Atlas Note"
          headline="One essay a week. About design careers. From working practitioners."
          supportingLine="Long-form essays from instructors and alumni. Real career stories, real critique tips, real industry shifts. 18,400 subscribers. Tuesday mornings, no promo."
          form={{
            fields: [{ name: "email", label: "Email", type: "email", placeholder: "you@example.com", required: true }],
            submitLabel: "Subscribe (free)",
            successMessage: "Welcome to The Atlas Note.",
          }}
          trustMicrocopy="No spam. One-click unsubscribe. We send less than we should."
        />
      </main>
      <MegaFooter
        brand="Atlas Learning"
        brandDescription="Live and self-paced design and product education for working practitioners. Founded 2019. 1,400+ alumni at companies that ship."
        newsletter={{
          heading: "Get the syllabus and The Atlas Note.",
          supportingLine: "Course syllabus + weekly essay. No spam, ever.",
          submitLabel: "Subscribe",
          microcopy: "By subscribing, you agree to our terms.",
        }}
        contact={{
          email: "hello@atlaslearning.co",
          hours: "Office hours: weekdays 9am–6pm PT",
        }}
        linkColumns={[
          {
            heading: "Programs",
            items: [
              { label: "Product Design Fundamentals", href: "#fundamentals" },
              { label: "Senior Design Systems", href: "#systems" },
              { label: "Design Engineering", href: "#design-eng" },
              { label: "Research Operations", href: "#research" },
              { label: "Portfolio Studio", href: "#portfolio" },
              { label: "Career Coaching", href: "#coaching" },
            ],
          },
          {
            heading: "Learn more",
            items: [
              { label: "Syllabus", href: "/syllabus" },
              { label: "Instructors", href: "/instructors" },
              { label: "Hiring partners", href: "#partners" },
              { label: "Alumni network", href: "#alumni" },
              { label: "The Atlas Note", href: "/newsletter" },
              { label: "FAQ", href: "/faq" },
            ],
          },
          {
            heading: "Applying",
            items: [
              { label: "Apply to a cohort", href: "#apply" },
              { label: "Buy self-paced", href: "#self-paced" },
              { label: "Team & corporate", href: "#team-pricing" },
              { label: "Refund policy", href: "/refunds" },
              { label: "Scholarships (TBA)", href: "/scholarships" },
            ],
          },
          {
            heading: "Company",
            items: [
              { label: "About us", href: "/about" },
              { label: "How we teach", href: "/method" },
              { label: "Careers", href: "/careers" },
              { label: "Contact", href: "/contact" },
              { label: "Press kit", href: "/press" },
            ],
          },
        ]}
        socialLinks={[
          { network: "twitter", href: "https://twitter.com/atlaslearning", label: "Twitter / X" },
          { network: "linkedin", href: "https://linkedin.com/company/atlaslearning", label: "LinkedIn" },
          { network: "youtube", href: "https://youtube.com/@atlaslearning", label: "YouTube" },
        ]}
        legalLinks={[
          { label: "Privacy", href: "/privacy" },
          { label: "Terms", href: "/terms" },
          { label: "Cookies", href: "/cookies" },
          { label: "Accessibility", href: "/accessibility" },
        ]}
        foundedYear="2019"
        organization={{
          name: "Atlas Learning",
          url: "https://atlaslearning.co",
          contactType: "Admissions",
        }}
      />
    </ThemeScope>
  )
}
